import React, { useState } from "react";

import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {
    InputChangeEvent,
    IScheduledTaskPropertyRef
} from "components/pages/scheduled-task-airflow/components/InputFields";
import { IPair } from "models/dataset-models";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup, ConfirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";

interface ITaskConfigNamedDatasetTableProps extends IScheduledTaskPropertyRef {
    header: string;
    datasets: Record<string, string>
    onChange: (e: InputChangeEvent<Record<string, string>>) => void;
    bodyClass?: string;
}

interface INamedDatasetRowModel {
    key: string,
    value: string | undefined,
    newKey: string,
    newValue: string | undefined,
    editMode: boolean,
}

export default function TaskConfigNamedDatasetTable(props: ITaskConfigNamedDatasetTableProps) {
    const [dataArr, setDataArr] = useState<INamedDatasetRowModel[]>(mapToList(props.datasets));

    const propagateChangesStateUp = (arr: IPair[]) => {
        const c: Record<string, string> = {}

        arr.forEach((e) => {
            if(e.value) c[e.key] = e.value; // identifier must be defined
        })

        props.onChange({property: props.property, value: c});
    }

    const confirmWrapper = (event: any, confirmHandler: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you really want to delete this dataset mapping?',
            icon: 'pi pi-exclamation-triangle',
            accept: confirmHandler,
        });
    }

    const handleEdit = (row: INamedDatasetRowModel) => {
        const idx = dataArr.indexOf(row)

        dataArr[idx].editMode = true
        dataArr[idx].newValue = dataArr[idx].value
        dataArr[idx].newKey = dataArr[idx].key
        setDataArr([...dataArr])
    }

    const handleEditCancel = (row: INamedDatasetRowModel) => {
        const idx = dataArr.indexOf(row)

        // Check if item is selected, if not remove row
        if(row.newValue == null || row.value == null) {
            setDataArr([...dataArr.filter(item => item.key !== row.key)]);
            return
        }

        dataArr[idx].editMode = false
        dataArr[idx].newValue = dataArr[idx].value
        dataArr[idx].newKey = dataArr[idx].key
        setDataArr([...dataArr])
    }

    const handleEditConfirm = (row: INamedDatasetRowModel) => {
        const idx = dataArr.indexOf(row)

        if(dataArr[idx].newValue === undefined) {
            easasAlert({
              summary: "Invalid",
              severity: "warn",
              detail: "Dataset must be selected!",
              life: 3000,
            });
            return
        }

        dataArr[idx].editMode = false
        dataArr[idx].value = dataArr[idx].newValue
        dataArr[idx].key = dataArr[idx].newKey

        const updated = [...dataArr]
        setDataArr(updated)
        propagateChangesStateUp(updated)
    }

    const handleAdd = () => {
        const updated = [...dataArr, { newKey: `new_dataset_${  dataArr.length}`, newValue: undefined, editMode: true} as INamedDatasetRowModel]
        setDataArr(updated);
    }

    const handleDelete = (row: INamedDatasetRowModel) => {
        const filtered = [...dataArr.filter(item => item.key !== row.key)]
        setDataArr(filtered);
        propagateChangesStateUp(filtered)
    }

    const handleValueChange = (e: any, row: INamedDatasetRowModel) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].newValue = e
        setDataArr([...dataArr])
    }

    const handleKeyChange = (e: any, row: INamedDatasetRowModel) => {
        const idx = dataArr.indexOf(row)
        dataArr[idx].newKey = e.target.value
        setDataArr([...dataArr])
    }

    const headerTemplate = (
        <div className="flex justify-content-between">
            <h4>{props.header}</h4>
            <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()}/>
        </div>
    );

    const keyBodyTemplate = (row: INamedDatasetRowModel) => {
        return <>
            { !row.editMode &&
                <div>
                    {row.key}
                </div>
            }

            { row.editMode &&
                <InputText placeholder="Key" value={row.newKey || ''}  onChange={ e => handleKeyChange(e, row) }/>
            }
        </>
    }

    const valueBodyTemplate = (row: INamedDatasetRowModel) => {
        return <>
            { !row.editMode &&
                <DatasetTitle identifier={row.value!!}/>
            }

            { row.editMode &&
                <DatasetDropdown value={row.newValue} onChange={ e => handleValueChange(e, row) }/>
            }
        </>
    }


    const actionBodyTemplate = (row: INamedDatasetRowModel) => {
        return <>
            { !row.editMode &&
                <Button icon="pi pi-pencil" className="p-button-info p-button-rounded p-button-text" onClick={() => handleEdit(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-check" className="p-button-info p-button-rounded p-button-text" onClick={() => handleEditConfirm(row)} />
            }

            { row.editMode &&
                <Button icon="pi pi-times" className="p-button-plain p-button-rounded p-button-text" onClick={() => handleEditCancel(row)} />
            }

            <Button icon="pi pi-trash" className="const-del-btn  p-button-rounded p-button-text p-button-danger" onClick={(e) => confirmWrapper(e, () => handleDelete(row))} />
        </>
    }


    return <>

        <DataTable value={dataArr}
                   responsiveLayout="scroll"
                   header={headerTemplate}
                   width="100%"
                   className={props.bodyClass}
                   size="small"
        >
            <Column header="Key" body={keyBodyTemplate} style={{width: "20rem"}}/>
            <Column header="Value" body={valueBodyTemplate}/>
            <Column body={actionBodyTemplate} bodyStyle={{textAlign: "right"}}/>
        </DataTable>

        <ConfirmPopup />
    </>
}

function mapToList(map: Record<string, string>): INamedDatasetRowModel[] {
    return Object.keys(map).map((k) => {
        return { key: k, value: map[k], editMode: false} as INamedDatasetRowModel;
    })
}